import {Injectable, NgZone, OnDestroy} from "@angular/core";
import {App} from "@capacitor/app";
import {PluginListenerHandle} from "@capacitor/core";
import {Router} from "@angular/router";
import {environment} from "src/environments/environment";
import {PlatformService} from "src/app/services/platform.service";
import {rememberRecoveryToken} from "../utils/recovery-token.util";
import {capturePublicWorkToken} from "../../works/utils/public-work-token.util";
import {isPublicWorkLink} from "../../works/utils/public-work-link.util";

@Injectable({
  providedIn: "root",
})
export class ActivationDeepLinkService implements OnDestroy {
  private listener?: Promise<PluginListenerHandle>;
  private readonly handledUrls = new Set<string>();
  private readonly frontOrigin = new URL(environment.frontUrl).origin;
  private navigationSequence = 0;
  private destroyed = false;

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly platformService: PlatformService
  ) {}

  public init(): void {
    if (!this.platformService.isNativePlatform() || this.listener) {
      return;
    }

    this.listener = App.addListener("appUrlOpen", ({url}) => {
      this.navigateToActivation(url);
    });

    void App.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        this.navigateToActivation(launchUrl.url);
      }
    });
  }

  private navigateToActivation(url: string): void {
    if (this.destroyed) return;
    let parsedUrl: URL;

    try {
      parsedUrl = new URL(url);
    } catch {
      return;
    }

    if (parsedUrl.origin === this.frontOrigin && isPublicWorkLink(parsedUrl)) {
      const route = capturePublicWorkToken(parsedUrl);
      this.ngZone.run(() => {
        void this.router.navigateByUrl(route);
      });
      return;
    }

    if (
      parsedUrl.origin !== this.frontOrigin ||
      !/^\/(account-activation|reset-password|email-change)(?:\/[a-zA-Z0-9]+)?$/.test(
        parsedUrl.pathname
      )
    ) {
      return;
    }

    if (this.handledUrls.has(url)) {
      return;
    }
    const route = rememberRecoveryToken(parsedUrl);
    if (!route) return;
    this.handledUrls.add(url);
    this.ngZone.run(() => {
      // A public sequence makes a second link refresh an already open recovery page.
      void this.router.navigateByUrl(
        `${route};request=${++this.navigationSequence}`
      );
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    void this.listener
      ?.then((listener) => listener.remove())
      .catch(() => undefined);
    this.handledUrls.clear();
  }
}
